import { UniversalLoader } from "./UniversalLoader";
import * as _ from "lodash";

import { CubeTexture, Material, MeshStandardMaterial, Texture } from "three";

export interface IMaterialCache {
  [materialName: string]: Material;
}

export interface IMaterialFactory {
  create(materialName: string): Material;
}

export interface IDelegateMaterialFactory {
  create(materialName: string): Material;
  replaceMaterial?(material: Material): void;
}

export type TextureLoadCallback = (texture: Texture) => void;
export type TextureErrorCallback = (error: Error | unknown) => void;

/**
 * Instanzen dieser Klasse müssen createMaterial implementieren und können an UniversalLoader.setMaterialFactory
 * übergeben werden. Somit kann z.B. einmalig eine MTL Datei geladen werden, oder die Materialien werden
 * programmatisch erzeugt. Es können auch Factories per Delegation verkettet werden. Falls diese
 * Instanz kein Material liefert, wird an die delegateMaterialFactory weitergeleitet
 */
export class AbstractMaterialFactory {
  texture_root: string;
  delegateMaterialFactory: IDelegateMaterialFactory | null;
  materials: IMaterialCache;

  /**
   * @param texture_root Der Basispfad, aus dem die Texturen geladen werden
   * @param delegateMaterialFactory Falls diese Instanz kein Material zu einem Namen liefert, wird an die delegateMaterialFactory weitergeleitet
   */
  constructor(
    texture_root: string,
    delegateMaterialFactory: IDelegateMaterialFactory | null = null,
  ) {
    this.texture_root = texture_root;
    this.delegateMaterialFactory = delegateMaterialFactory;
    this.materials = {};
  }

  /**
   * Erzeugt ein Material zu einem Namen, falls es nicht im Cache ist.
   * Delegiert an die delegateMaterialFactory weiter, wenn createMaterial_(materialName) kein Material liefert.
   */
  create(materialName: string): Material {
    materialName = materialName || "default";
    if (!this.materials[materialName]) {
      this.materials[materialName] = this._createMaterial(materialName);
    }
    if (!this.materials[materialName] && this.delegateMaterialFactory) {
      this.materials[materialName] = this.delegateMaterialFactory.create(materialName);
    }
    if (this.materials[materialName] == null) {
      this.materials[materialName] = new MeshStandardMaterial();
      this.materials[materialName].name = materialName;
      (
        this.materials[materialName].userData as { _no_material_with_this_name_found?: string }
      )._no_material_with_this_name_found = materialName;
      // logging ausgeschaltet, weil Normalfall bei externen Objekten mit enthaltenen Materialien (GTLF)
      // console.warn('MaterialFactory: found no material named '+materialName);
    }
    return this.materials[materialName];
  }

  /**
   * Muss überschrieben werden
   */
  _createMaterial(materialName: string): Material {
    throw new Error("not yet implemented");
  }

  /** */
  loadTextureCube(
    images: string[],
    onLoad?: TextureLoadCallback,
    onError?: TextureErrorCallback,
  ): CubeTexture | null {
    return UniversalLoader.loadTextureCube(
      images.map((img: string): string => this.getTextureUrl(img)),
      undefined,
      onLoad,
      onError,
    );
  }

  /** */
  loadTexture(path: string, onLoad?: TextureLoadCallback, onError?: TextureErrorCallback): Texture {
    let url = this.getTextureUrl(path);
    return UniversalLoader.loadTexture(url, undefined, onLoad, onError);
  }

  getTextureUrl(path: string): string {
    return /^(http|https|file):\/\/.*$/.test(path) ? path : this.texture_root + path;
  }

  /** */
  clearMaterialsCache(): void {
    this.materials = {};
  }

  /**
   * Muss überschrieben werden
   */
  getAllKnownMaterialNames(): string[] {
    throw new Error("not yet implemented");
  }

  /**
   */
  replaceMaterial(material: Material): void {
    if (this.materials[material.name]) {
      this.materials[material.name] = material;
    }
    if (this.delegateMaterialFactory?.replaceMaterial) {
      this.delegateMaterialFactory.replaceMaterial(material);
    }
  }
}
